import React, { useState, useRef, useEffect, useCallback } from "react";
import moment from "moment";
import { SvgIcons } from "@tripian/react";
import useTranslate from "../../../hooks/useTranslate";
import { useIsMobile } from "../../../hooks/useIsMobile";

interface ValaiDateRangePickerProps {
  startDate: moment.Moment;
  endDate: moment.Moment;
  onDateChange: (startDate: moment.Moment, endDate: moment.Moment) => void;
  className?: string;
  maxDays?: number;
  hideTimeSelect?: boolean;
}

const generateTimeOptions = () => {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      options.push(`${h}:${m}`);
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const roundToNearestTimeOption = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  const roundedMin = m < 30 ? '00' : '30';
  return `${h.toString().padStart(2, '0')}:${roundedMin}`;
};

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const ValaiDateRangePicker: React.FC<ValaiDateRangePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
  className = "",
  maxDays,
  hideTimeSelect,
}) => {
  const { t } = useTranslate();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [selectingStart, setSelectingStart] = useState(true);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [startTime, setStartTime] = useState(roundToNearestTimeOption(startDate.format('HH:mm')));
  const [endTime, setEndTime] = useState(roundToNearestTimeOption(endDate.format('HH:mm')));
  const [currentMonth, setCurrentMonth] = useState(moment(startDate).startOf('month'));
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempStartDate(startDate);
    setStartTime(roundToNearestTimeOption(startDate.format('HH:mm')));
  }, [startDate]);

  useEffect(() => {
    setTempEndDate(endDate);
    setEndTime(roundToNearestTimeOption(endDate.format('HH:mm')));
  }, [endDate]);

  // Close on outside click (desktop)
  useEffect(() => {
    if (isMobile || !isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, isOpen]);

  // Body scroll lock for mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isMobile, isOpen]);

  const getMaxEndDate = useCallback((start: moment.Moment): moment.Moment | null => {
    if (!maxDays) return null;
    return start.clone().add(maxDays - 1, 'days');
  }, [maxDays]);

  const handleOpen = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setStartTime(roundToNearestTimeOption(startDate.format('HH:mm')));
    setEndTime(roundToNearestTimeOption(endDate.format('HH:mm')));
    setCurrentMonth(startDate.clone().startOf('month'));
    setSelectingStart(true);
    setIsOpen(true);
  };

  const handleDateClick = (date: moment.Moment) => {
    if (selectingStart) {
      const [h, m] = startTime.split(':').map(Number);
      const newStart = date.clone().set({ hour: h, minute: m });
      setTempStartDate(newStart);
      // Clear end date — user will pick it next
      setTempEndDate(newStart.clone());
      setSelectingStart(false);
    } else {
      if (date.isBefore(tempStartDate, 'day') || date.isSame(tempStartDate, 'day')) {
        const [h, m] = startTime.split(':').map(Number);
        setTempStartDate(date.clone().set({ hour: h, minute: m }));
      } else {
        const [endH, endM] = endTime.split(':').map(Number);
        setTempEndDate(date.clone().set({ hour: endH, minute: endM }));
        setSelectingStart(true);
      }
    }
  };

  const isDateDisabled = (date: moment.Moment): boolean => {
    const today = moment();
    if (date.isBefore(today, 'day')) return true;
    if (!selectingStart) {
      const maxEnd = getMaxEndDate(tempStartDate);
      if (maxEnd && date.isAfter(maxEnd, 'day')) return true;
    }
    return false;
  };

  const handleApply = () => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const finalStart = tempStartDate.clone().set({ hour: startHour, minute: startMin });
    const finalEnd = tempEndDate.clone().set({ hour: endHour, minute: endMin });
    onDateChange(finalStart, finalEnd);
    setIsOpen(false);
  };

  const renderMonth = (monthMoment: moment.Moment) => {
    const startOfMonth = monthMoment.clone().startOf('month');
    const endOfMonth = monthMoment.clone().endOf('month');
    const startDay = startOfMonth.clone().startOf('week');
    const endDay = endOfMonth.clone().endOf('week');

    const weeks: moment.Moment[][] = [];
    let days: moment.Moment[] = [];
    let day = startDay.clone();

    while (day.isBefore(endDay) || day.isSame(endDay, 'day')) {
      days.push(day.clone());
      if (days.length === 7) {
        weeks.push(days);
        days = [];
      }
      day.add(1, 'day');
    }

    const today = moment();

    return (
      <div className="flex-1 min-w-0">
        <div className="text-center text-sm font-semibold text-gray-800 mb-2">
          {monthMoment.format('MMMM YYYY')}
        </div>
        <div className="grid grid-cols-7 gap-0.5 mb-1">
          {WEEKDAYS.map(dayName => (
            <div key={dayName} className="text-center text-[11px] font-medium text-gray-400 py-1">
              {dayName}
            </div>
          ))}
        </div>
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-0.5">
            {week.map((dayItem, dayIndex) => {
              const isCurrentMonth = dayItem.month() === monthMoment.month();
              const isToday = dayItem.isSame(today, 'day');
              const isStart = dayItem.isSame(tempStartDate, 'day');
              const isEnd = dayItem.isSame(tempEndDate, 'day');
              const isInRange = dayItem.isAfter(tempStartDate, 'day') && dayItem.isBefore(tempEndDate, 'day');
              const disabled = isDateDisabled(dayItem);

              return (
                <button
                  key={dayIndex}
                  onClick={() => !disabled && isCurrentMonth && handleDateClick(dayItem)}
                  disabled={disabled || !isCurrentMonth}
                  className={`
                    py-1.5 text-xs rounded-md transition-colors
                    ${!isCurrentMonth ? 'text-gray-200 cursor-default' : ''}
                    ${disabled && isCurrentMonth ? 'text-gray-300 cursor-not-allowed' : ''}
                    ${!disabled && isCurrentMonth ? 'hover:bg-gray-100' : ''}
                    ${isToday && !isStart && !isEnd ? 'border border-primary-color' : ''}
                    ${isStart || isEnd ? 'bg-primary-color ' : ''}
                    ${isInRange && isCurrentMonth ? 'bg-primary-color-50' : ''}
                  `}
                >
                  {dayItem.date()}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const nextMonth = currentMonth.clone().add(1, 'month');
  const arrivalLabel = 'Start Date';
  const departureLabel = 'End Date';
  const selectionHint = selectingStart ? arrivalLabel : departureLabel;

  const calendarContent = (
    <>
      {/* Selection hint */}
      <div className="text-center text-xs text-gray-500 px-3 pt-3 pb-1">
        {selectionHint}
      </div>

      {/* Navigation + Calendars */}
      <div className="px-3">
        <div className="flex items-center justify-between mb-1">
          <button
            onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, 'month'))}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => setCurrentMonth(currentMonth.clone().add(1, 'month'))}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className={`flex ${isMobile ? 'flex-col gap-4' : 'gap-4'}`}>
          {renderMonth(currentMonth)}
          {renderMonth(nextMonth)}
        </div>
      </div>

      {/* Time Selection */}
      {!hideTimeSelect && (
        <div className="mx-3 mt-3 pt-3 border-t border-gray-200">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">{arrivalLabel}</label>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-600">{tempStartDate.format('MMM D')}</span>
                <select
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-primary-color bg-white"
                >
                  {TIME_OPTIONS.map((time) => (
                    <option key={`start-${time}`} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">{departureLabel}</label>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-600">{tempEndDate.format('MMM D')}</span>
                <select
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:border-primary-color bg-white"
                >
                  {TIME_OPTIONS.map((time) => (
                    <option key={`end-${time}`} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Apply */}
      <div className={`px-3 pb-3 ${hideTimeSelect ? 'mt-3 pt-3 border-t border-gray-200' : 'mt-3'}`}>
        <button
          onClick={handleApply}
          className="w-full py-2 bg-primary-color text-white rounded-full text-sm font-medium hover:bg-primary-color/90 transition-colors"
        >
          {t('trips.createNewTrip.form.continue')}
        </button>
      </div>
    </>
  );

  const dateFormat = hideTimeSelect ? 'DD.MM.YYYY' : 'MMM D, HH:mm';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={handleOpen}
        className={`flex items-center gap-3 px-4 w-full text-left ${hideTimeSelect ? 'bg-white border-2 border-gray-300 py-[0.7rem]' : 'bg-white border border-primary-color py-4'}`}
        style={{ borderRadius: '30px' }}
      >
        <SvgIcons.Calendar className="w-6 h-6 text-primary-color" />
        <div className="flex items-center flex-1">
          <span className={`flex-1 ${startDate ? 'text-gray-800' : 'text-gray-400'}`}>
            {startDate ? startDate.format(dateFormat) : arrivalLabel}
          </span>
          <div className="w-px h-6 bg-gray-300 mx-4" />
          <span className={`flex-1 ${endDate ? 'text-gray-800' : 'text-gray-400'}`}>
            {endDate ? endDate.format(dateFormat) : departureLabel}
          </span>
        </div>
      </button>

      {/* Desktop: Dropdown */}
      {isOpen && !isMobile && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-50 w-[480px]">
          {calendarContent}
        </div>
      )}

      {/* Mobile: Full-screen modal */}
      {isOpen && isMobile && (
        <div className="fixed inset-0 bg-white z-[9999] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
            <span className="font-semibold text-gray-800">{selectionHint}</span>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {calendarContent}
          </div>
        </div>
      )}
    </div>
  );
};

export default ValaiDateRangePicker;
